import { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import {
  DoughnutChart,
  DropdownMenu,
  DropdownMenuOption,
  PaginationProps,
  PaginationTwo,
  TableBodyRow,
} from "components/";
import { Button } from "components/button";
import { DataTable } from "components/data-table";
import styles from "./styles.module.scss";
import { ChevronRightIcon, TriangleEmergencyIcon, CopyIcon } from "assets";
import { DecisionBucket, formatMetricLabel } from "hooks";
import * as Popover from "@radix-ui/react-popover";

export interface AssessmentTableItem {
  id: string;
  name: string;
  email: string;
  date: string;
  cv: boolean;
  cvUrl?: string;
  score: number;
  status: string;
  decision: string;
  /** Score per filtered metric, keyed by metric key. null = none reported. */
  metricScores?: Record<string, number | null>;
}

// A candidate's attempt is treated as disrupted (and therefore retake-eligible)
// only when they completed the assessment but scored zero — e.g. an attempt that
// was auto-submitted after a tab-switch violation or when the timer ran out.
// Zero-score attempts that are still in progress or were abandoned are NOT
// disrupted, so they keep the normal decision flow.
const isDisrupted = (item: Pick<AssessmentTableItem, 'score' | 'status'>) =>
  item.score === 0 && item.status === 'completed';

// A candidate is still taking the assessment while the backend reports the
// `started` state. There is no report or score yet, so we surface an
// "In-progress" indicator, render "N/A" for the score, and keep the row
// non-clickable.
const isInProgress = (item: Pick<AssessmentTableItem, 'status'>) =>
  item.status === 'started';

// A candidate who left without finishing is reported in the `abandoned` state.
// A (partial) report still exists for whatever they answered, so recruiters can
// open it — the row stays clickable and shows the candidate's score. We still
// surface an "Abandoned" indicator in place of the decision control.
const isAbandoned = (item: Pick<AssessmentTableItem, 'status'>) =>
  item.status === 'abandoned';

interface TableBodyProps {
  tableBodyItems: AssessmentTableItem[];
  handleView: (id: string) => void;
  /** @deprecated No longer used */
  tableBodyItemClassName?: string;
  /** @deprecated No longer used */
  tableBodyRowClassName?: string;
  pagination?: PaginationProps;
  currentFilter: string;
  /**
   * Metrics the recruiter is filtering on. When any are selected they take
   * over the score column, since the per-metric scores say everything the
   * overall score did and more.
   */
  selectedMetrics?: string[];
  checks: boolean[];
  handleCheck: ({ index, value }: { index: number; value: boolean }) => void;
  decisionBuckets?: DecisionBucket[];
  onDecisionChange?: (candidateId: string, decision: string) => void;
  onDownloadCV?: (id: string, cvUrl: string) => void;
  onAllowRetake?: (id: string) => void;
  emptyState?: React.ReactNode;
  checkAll?: boolean;
  onCheckAllChange?: () => void;
  copyChecks?: boolean[];
  handleCopyCheck?: ({ index, value }: { index: number; value: boolean }) => void;
  copyCheckAll?: boolean;
  onCopyCheckAllChange?: () => void;
}

const DisruptedTooltip = () => {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        asChild
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="flex-shrink-0 cursor-help inline-flex items-center">
          <TriangleEmergencyIcon className="w-3 h-3" />
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="z-[10000] w-[220px] rounded-lg bg-[#1D2939] p-3 text-xs leading-snug text-white shadow-lg outline-none"
        >
          This candidate&apos;s previous attempt appears disrupted. You can
          allow them retake the assessment
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const InProgressIndicator = () => {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        asChild
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="cursor-help text-[#1570EF] whitespace-nowrap px-3 py-1.5 rounded-full bg-[#EFF8FF] text-xs">
          In-progress
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={6}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="z-[10000] w-[240px] rounded-lg bg-[#1D2939] p-3 text-xs leading-snug text-white shadow-lg outline-none"
        >
          This candidate is currently taking the assessment. The report will be
          generated upon completion.
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const AbandonedIndicator = () => {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        asChild
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="cursor-help text-[#B42318] whitespace-nowrap px-3 py-1.5 rounded-full bg-[#FEF3F2] text-xs">
          Abandoned
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={6}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="z-[10000] w-[240px] rounded-lg bg-[#1D2939] p-3 text-xs leading-snug text-white shadow-lg outline-none"
        >
          This candidate left before completing the assessment. A partial
          report was generated from what they answered — open the row to view
          their score.
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

// Decision cell content: wide enough for the longest bucket label ("Request
// More Info") plus its chevron, so every badge starts on the same edge instead
// of drifting with the length of its own label.
const DECISION_CONTENT_CLASS = "inline-block text-left min-w-[154px]";

// The metric chips need noticeably more room than the score doughnut they
// replace, so the table's minimum width grows with them rather than squeezing
// the name and decision columns.
const tableMinWidthClass = (showSelectedMetrics: boolean) =>
  showSelectedMetrics ? "min-w-[1040px]" : "min-w-[860px]";

// Metric chips reuse the score bands from the doughnut, so a chip and a score
// can never disagree about what counts as a strong result.
const metricScoreColorClass = (score: number | null): string => {
  if (score === null) return "text-[#98A2B3]";
  if (score >= 90) return "text-[#18B368]";
  if (score >= 70) return "text-[#1570EF]";
  if (score >= 50) return "text-[#DB8400]";
  return "text-[#E74444]";
};

const MetricChip = ({ label, score }: { label: string; score: number | null }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#B2DDFF] px-2 py-[2px]">
    <span className="text-[11px] leading-[16px] text-[#475467]">{label}</span>
    <span className={`text-[11px] leading-[16px] font-semibold ${metricScoreColorClass(score)}`}>
      {score === null ? "—" : `${score}%`}
    </span>
  </span>
);

const MetricChips = ({
  metrics,
  scores,
  className = "",
}: {
  metrics: string[];
  scores?: Record<string, number | null>;
  className?: string;
}) => (
  <span className={`flex flex-wrap items-center gap-1.5 ${className}`}>
    {metrics.map((metric) => (
      <MetricChip
        key={metric}
        label={formatMetricLabel(metric)}
        score={scores?.[metric] ?? null}
      />
    ))}
  </span>
);

const decisionStyleMap: Record<string, string> = {
  Interview: styles.interview,
  Shortlisted: styles.shortlisted,
  "Probe further": styles.probe,
  Revisit: styles.revisit,
  Review: styles.review,
  "Request More Info": styles.request_more_info,
  "Save for future": styles.save_for_future,
  Reject: styles.reject,
  Rejected: styles.rejected,
  Assessed: styles.assessed,
};

const AssessmentTable: React.FC<TableBodyProps> = ({
  tableBodyItems,
  handleView,
  pagination,
  selectedMetrics = [],
  checks,
  handleCheck,
  decisionBuckets = [],
  onDecisionChange,
  onDownloadCV,
  onAllowRetake,
  emptyState,
  checkAll,
  onCheckAllChange,
  copyChecks = [],
  handleCopyCheck,
  copyCheckAll,
  onCopyCheckAllChange,
}) => {
  const handleDownloadCV = (id: string, cvUrl?: string) => {
    if (cvUrl && onDownloadCV) onDownloadCV(id, cvUrl);
  };

  const handleDecisionUpdate = (candidateId: string, decision: string) => {
    onDecisionChange?.(candidateId, decision);
  };

  const showSelectedMetrics = selectedMetrics.length > 0;
  const minWidthClass = tableMinWidthClass(showSelectedMetrics);

  const getDecisionOptions = (itemId: string): DropdownMenuOption[] =>
    decisionBuckets.map((bucket) => {
      const isReject = bucket.decision.toLowerCase().includes("reject");
      return {
        text: isReject ? (
          <span className={styles.delete}>{bucket.decision}</span>
        ) : (
          bucket.decision
        ),
        onClick: () => handleDecisionUpdate(itemId, bucket.decision),
        key: bucket.decision.toLowerCase().replace(" ", "_"),
      };
    });

  // Score chart — shown while no metric filter is active.
  const scoreColumn: ColumnDef<AssessmentTableItem, unknown> = {
    id: "score",
    header: () => (
      <span
        data-tour-id="tour-score-column"
        style={{
          display: 'inline-flex',
          width: 'fit-content',
          alignItems: 'center',
          justifyContent: 'flex-start',
          lineHeight: 1,
        }}
      >
        Score
      </span>
    ),
    meta: { className: "w-16 text-[#1D2939] !font-medium", cellClassName: "flex items-center justify-center" },
    cell: ({ row }) => (
      <span
        className={`${styles.scoreWrap} -mt-1.5 -ml-1`}
        onClick={(e) => e.stopPropagation()}
      >
        <DoughnutChart
          cutout="76%"
          className={styles.score}
          score={isInProgress(row.original) ? 0 : row.original.score}
        />
        <p
          className={`${
            isInProgress(row.original) || row.original.score === 0
              ? styles.grey
              : row.original.score >= 90
              ? styles.green
              : row.original.score >= 70
              ? styles.blue
              : row.original.score >= 50
              ? styles.orange
              : styles.red
          }${isInProgress(row.original) ? ` ${styles.naScore}` : ''}`}
        >
          {isInProgress(row.original) ? 'N/A' : row.original.score}
        </p>
      </span>
    ),
  };

  // Selected Metrics — takes the score column's place while the recruiter is
  // filtering by metric. It keeps the score tour step's anchor so the tour
  // still has something to point at.
  const selectedMetricsColumn: ColumnDef<AssessmentTableItem, unknown> = {
    id: "selectedMetrics",
    header: () => (
      <span data-tour-id="tour-score-column" style={{ display: 'inline-block' }}>
        Selected Metrics
      </span>
    ),
    meta: { className: "text-[12px] text-[#1D2939] !font-medium w-[280px]" },
    cell: ({ row }) => (
      <span onClick={(e) => e.stopPropagation()}>
        <MetricChips
          metrics={selectedMetrics}
          scores={row.original.metricScores}
          className="max-w-[256px]"
        />
      </span>
    ),
  };

  const desktopColumns: ColumnDef<AssessmentTableItem, unknown>[] = [
    // Row-select checkbox
    {
      id: "select",
      header: () => (
        <span>
          <input
            className={`ml-3 ${styles.check}`}
            onChange={onCheckAllChange}
            checked={checkAll ?? false}
            type="checkbox"
          />
        </span>
      ),
      meta: { className: "w-8" },
      cell: ({ row }) => (
        <span onClick={(e) => e.stopPropagation()}>
          <input
            className={`ml-3 ${styles.check}`}
            onChange={() =>
              handleCheck({ index: row.index, value: !checks[row.index] })
            }
            checked={checks[row.index] ?? false}
            type="checkbox"
          />
        </span>
      ),
    },
    // Serial number
    {
      id: "sn",
      header: "S/N",
      meta: { className: "w-8 text-[12px] text-[#1D2939] !font-medium" },
      cell: ({ row }) => (
        <span className="text-[12px] text-[#1D2939] !font-medium text-[#667085]">{row.index + 1}</span>
      ),
    },
    // Candidate name + email
    {
      id: "name",
      header: "Name",
      meta: { className: "text-[12px] text-[#1D2939] !font-medium" },
      cell: ({ row }) => (
        <span className="flex flex-col overflow-hidden w-full">
          <span className="flex items-center gap-1.5">
            <span className={styles.name}>{row.original.name}</span>
            {isDisrupted(row.original) && <DisruptedTooltip />}
          </span>
          <span className={styles.email}>{row.original.email}</span>
        </span>
      ),
    },
    // Date
    {
      id: "date",
      header: "Date",
      meta: { className: "text-[12px] text-[#1D2939] !font-medium whitespace-nowrap" },
      cell: ({ row }) => (
        <span className={styles.date}>{row.original.date}</span>
      ),
    },
    // CV batch-select checkbox
    {
      id: "copyCheck",
      header: () => (
        <span>
          <input
            className={`ml-3 ${styles.check}`}
            onChange={onCopyCheckAllChange}
            checked={copyCheckAll ?? false}
            type="checkbox"
          />
        </span>
      ),
      meta: { className: "w-8" },
      cell: ({ row }) => (
        <span onClick={(e) => e.stopPropagation()}>
          <input
            className={`ml-3 ${styles.check}`}
            onChange={() =>
              handleCopyCheck?.({ index: row.index, value: !copyChecks[row.index] })
            }
            checked={copyChecks[row.index] ?? false}
            type="checkbox"
          />
        </span>
      ),
    },
    // CV download button
    {
      id: "cv",
      header: "CV",
      meta: { className: "whitespace-nowrap text-[12px] text-[#1D2939] !font-medium" },
      cell: ({ row }) => (
        <span onClick={(e) => e.stopPropagation()}>
          {row.original.cv ? (
            <Button
              type="transparent"
              onClick={() => handleDownloadCV(row.original.id, row.original.cvUrl)}
              className={styles.downloadButton}
            >
              <CopyIcon className={styles.copyIcon} />
              Download
            </Button>
          ) : (
            <span className="text-[#98A2B3]">N/A</span>
          )}
        </span>
      ),
    },
    showSelectedMetrics ? selectedMetricsColumn : scoreColumn,
    // Decision dropdown
    {
      id: "decision",
      header: () => (
        <span className={DECISION_CONTENT_CLASS}>
          <span data-tour-id="tour-decision-column" style={{ display: 'inline-block' }}>Decision</span>
        </span>
      ),
      // Header and cells get the same treatment: centre the column's content,
      // then hold it to a fixed width and left-align within that. The cluster
      // sits mid-column, every badge starts on the same edge whatever its label,
      // and the header label lines up with them. The header needs !text-center
      // to beat the `text-left` the table puts on every <th>.
      // Do NOT reach for `display: flex` on the cell: a flexed <td> stops being
      // a table cell, and two adjacent ones merge into a single anonymous cell,
      // which stacks the score and the decision into one column.
      meta: {
        headerClassName: "text-[12px] text-[#1D2939] !font-medium !text-end",
        cellClassName: "text-[12px] text-[#1D2939] !font-medium align-middle text-end",
      },
      cell: ({ row }) => {
        const content = () => {
          // Still taking the assessment — no decision to set yet.
          if (isInProgress(row.original)) return <InProgressIndicator />;
          // Left without finishing — no report or decision to set.
          if (isAbandoned(row.original)) return <AbandonedIndicator />;
          // A retake is only offered for a disrupted attempt — see isDisrupted.
          if (isDisrupted(row.original)) {
            return (
              <button
                onClick={() => onAllowRetake?.(row.original.id)}
                className="px-3 py-1.5 rounded-md border border-[#175CD3] bg-white text-[#175CD3] text-xs font-medium cursor-pointer hover:bg-[#F9FAFB] whitespace-nowrap"
              >
                Allow Retake
              </button>
            );
          }
          return (
            <DropdownMenu
              options={getDecisionOptions(row.original.id)}
              trigger={
                <span className={`${styles.decision}`}>
                  <Button
                    type="fill"
                    onClick={() => {}}
                    className={`whitespace-nowrap ${styles.decisionButton} ${
                      decisionStyleMap[row.original.decision] || ""
                    }`}
                  >
                    {row.original.decision}
                  </Button>
                  <ChevronRightIcon className={styles.chevron} />
                </span>
              }
            />
          );
        };

        return (
          <span onClick={(e) => e.stopPropagation()} className={DECISION_CONTENT_CLASS}>
            {content()}
          </span>
        );
      },
    },
  ];

  const renderMobileRow = (item: AssessmentTableItem, index: number) => (
    <TableBodyRow
      key={`mobile-${index}`}
      style={{ zIndex: tableBodyItems.length - index }}
      customClassName={`!shadow-none !mb-2 !border !border-[#EAECF0] !rounded-lg mt-4 ${
        isDisrupted(item) ? "!bg-[#FFF5E6]" : ""
      } ${showSelectedMetrics ? "!flex-col !items-start" : ""}`}
      onClick={() => (!isDisrupted(item) && !isInProgress(item)) ? handleView(item.id) : undefined}
    >
      <div className="flex justify-between items-center w-full">
        <span className={`${styles.title} !w-[60%]`}>
          <span className="flex items-center gap-1.5">
            <span className={styles.name}>{item.name}</span>
            {isDisrupted(item) && <DisruptedTooltip />}
          </span>
          <span className={styles.email}>{item.email}</span>
          <span onClick={(e) => e.stopPropagation()}>
            {isInProgress(item) ? (
              <span className="mt-1 inline-block">
                <InProgressIndicator />
              </span>
            ) : isAbandoned(item) ? (
              <span className="mt-1 inline-block">
                <AbandonedIndicator />
              </span>
            ) : isDisrupted(item) ? (
              <button
                onClick={() => onAllowRetake?.(item.id)}
                className="mt-1 px-3 py-1 text-xs font-medium rounded-lg border border-[#D0D5DD] bg-white text-[#344054] cursor-pointer"
              >
                Allow Retake
              </button>
            ) : (
              <Popover.Root>
                <Popover.Trigger asChild>
                  <span className="text-[#175CD3] font-normal mt-1 flex items-center gap-2 cursor-pointer">
                    Decision
                    <ChevronRightIcon className="mt-1 -rotate-90 font-medium text-[#175CD3] [&>path]:stroke-[1px] [&>path]:stroke-[#175CD3]" />
                  </span>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    side="bottom"
                    align="start"
                    sideOffset={6}
                    collisionPadding={8}
                    data-assessment-decision-popover
                    className="bg-white rounded-lg shadow-lg border border-[#EAECF0] outline-none py-2 min-w-[175px]"
                  >
                    {decisionBuckets.map((bucket) => {
                      const isSelected = item.decision === bucket.decision;
                      const isReject = bucket.decision
                        .toLowerCase()
                        .includes("reject");
                      return (
                        <Popover.Close asChild key={bucket.id}>
                          <label
                            className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-[#F9FAFB]"
                            onClick={() =>
                              handleDecisionUpdate(item.id, bucket.decision)
                            }
                          >
                            <input
                              type="radio"
                              name={`decision-${item.id}`}
                              checked={isSelected}
                              onChange={() => {}}
                              className="w-4 h-4 accent-[#012169] flex-shrink-0"
                            />
                            <span
                              className={`text-sm ${
                                isReject ? styles.delete : "text-[#344054]"
                              }`}
                            >
                              {bucket.decision}
                            </span>
                          </label>
                        </Popover.Close>
                      );
                    })}
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            )}
          </span>
        </span>
        <span className={`${styles.scoreWrap} flex-shrink-0`}>
          <DoughnutChart
            cutout="76%"
            className={styles.score}
            score={isInProgress(item) ? 0 : item.score}
          />
          <p
            className={`${
              isInProgress(item) || item.score === 0
                ? styles.grey
                : item.score >= 90
                ? styles.green
                : item.score >= 70
                ? styles.blue
                : item.score >= 50
                ? styles.orange
                : styles.red
            }${isInProgress(item) ? ` ${styles.naScore}` : ''}`}
          >
            {isInProgress(item) ? 'N/A' : item.score}
          </p>
        </span>
      </div>
      {/* On mobile the chips get their own row rather than displacing the
          score — there is height to spare here, unlike in the desktop grid. */}
      {showSelectedMetrics && (
        <MetricChips
          metrics={selectedMetrics}
          scores={item.metricScores}
          className="w-full mt-3"
        />
      )}
    </TableBodyRow>
  );

  return (
    <>
      {/* Desktop */}
      <div className="sm:block hidden">
        <DataTable
          columns={desktopColumns}
          data={tableBodyItems}
          emptyState={emptyState}
          onRowClick={(item) => handleView(item.id)}
          getRowClickable={(item) => !isDisrupted(item) && !isInProgress(item)}
          rowBaseClassName={(item, index) =>
            `text-[12px] text-[#667085] border-t border-[#EAECF0] ${
              isDisrupted(item) ? "bg-[#FFF5E6]" : index % 2 === 0 ? "bg-[#F9FAFB]" : "bg-white"
            }`
          }
          cellBaseClassName="px-3 py-4"
          headerCellClassName="px-3 py-3 text-left"
          headerClassName="bg-white text-[13px] font-medium text-[#1D2939]"
          containerClassName={`${minWidthClass} border border-[#EAECF0] rounded-xl overflow-hidden`}
        />
      </div>

      {/* Mobile — unchanged */}
      <div className="sm:hidden block">
        {tableBodyItems.length === 0 && emptyState ? (
          <div>{emptyState}</div>
        ) : (
          tableBodyItems.map((item, index) => renderMobileRow(item, index))
        )}
      </div>

      {pagination && (
        <PaginationTwo {...pagination} className={styles.pagination} />
      )}
    </>
  );
};

export { AssessmentTable };
